import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Printer, Download, Plus, Trash2, FileText, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InvoiceGenerator = () => {
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, unitPrice: 0 }
  ]);

  const companyDetails = {
    name: 'AIS EXPRESS',
    address: '22 Osmans Close, Bracknell, England, RG42 7PX',
    phone: '01344 508095',
    email: 'customersupport@aisexpress.co.uk',
    companyReg: '12801331',
    vatNumber: '355228595',
    bankDetails: {
      accountName: 'AIS EXPRESS',
      accountNumber: '22434741',
      sortCode: '04-06-05',
      bankName: 'TIDE'
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unitPrice: 0 }]);
  };

  const removeLineItem = (index) => {
    const newItems = lineItems.filter((_, i) => i !== index);
    setLineItems(newItems);
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...lineItems];
    newItems[index][field] = value;
    setLineItems(newItems);
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0);
    }, 0);
  };

  const calculateVAT = () => {
    return calculateSubtotal() * 0.20; // 20% VAT
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateVAT();
  };

  const getDueDate = () => {
    const date = new Date(invoiceDate);
    date.setDate(date.getDate() + 14);
    return date.toISOString().split('T')[0];
  };

  const generateInvoiceNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 10000);
    setInvoiceNumber(`INV-${year}${month}-${random}`);
  };

  const printInvoice = () => {
    window.print();
  };

  const downloadInvoice = () => {
    // Create a new window with the invoice content
    const printWindow = window.open('', '_blank');
    const invoiceHTML = document.getElementById('invoice-content').innerHTML;
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice ${invoiceNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; }
            .invoice-container { max-width: 800px; margin: 0 auto; }
            .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
            .company-details { text-align: left; }
            .invoice-details { text-align: right; }
            .customer-details { margin-bottom: 30px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
            th { background-color: #10b981; color: white; }
            .totals { text-align: right; margin-top: 20px; }
            .totals-row { display: flex; justify-content: flex-end; margin: 8px 0; }
            .totals-label { margin-right: 20px; font-weight: bold; }
            .totals-value { width: 120px; text-align: right; }
            .bank-details { margin-top: 40px; padding: 20px; background-color: #f3f4f6; border-radius: 8px; }
            .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #6b7280; }
            .total-row { font-size: 18px; font-weight: bold; color: #10b981; }
          </style>
        </head>
        <body>
          ${invoiceHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const sendInvoiceEmail = async () => {
    // Validate required fields
    if (!invoiceNumber) {
      alert('Please enter an invoice number');
      return;
    }
    if (!customerName) {
      alert('Please enter customer name');
      return;
    }
    if (!customerEmail) {
      alert('Please enter customer email address');
      return;
    }
    if (!lineItems.some(item => item.description && item.unitPrice > 0)) {
      alert('Please add at least one line item with description and price');
      return;
    }

    try {
      setIsSending(true);

      const invoiceData = {
        invoice_number: invoiceNumber,
        invoice_date: new Date(invoiceDate).toLocaleDateString('en-GB'),
        due_date: new Date(getDueDate()).toLocaleDateString('en-GB'),
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        customer_address: customerAddress,
        line_items: lineItems.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unitPrice) || 0
        }))
      };

      const response = await axios.post(`${API}/admin/send-invoice`, invoiceData);

      if (response.data.success) {
        alert(`Invoice sent successfully to ${customerEmail}!`);
      } else {
        alert(`Failed to send invoice: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      alert('Failed to send invoice. Please check your email configuration.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Invoice Form */}
      <Card className="shadow-lg print:hidden">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Create Invoice</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Invoice Number */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Invoice Number *
              </label>
              <div className="flex gap-2">
                <Input
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="INV-202501-0001"
                  className="flex-1"
                />
                <Button
                  onClick={generateInvoiceNumber}
                  variant="outline"
                  className="whitespace-nowrap"
                >
                  Generate
                </Button>
              </div>
            </div>

            {/* Invoice Date */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Invoice Date *
              </label>
              <Input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </div>
          </div>

          {/* Customer Details */}
          <div className="border-t border-slate-200 pt-6 mb-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Customer Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Customer Name *
                </label>
                <Input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email
                </label>
                <Input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="customer@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Phone
                </label>
                <Input
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="01234 567890"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Address
                </label>
                <Input
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  placeholder="123 Street, City, Postcode"
                />
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="border-t border-slate-200 pt-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Services / Items</h3>
              <Button
                onClick={addLineItem}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </div>

            <div className="space-y-3">
              {lineItems.map((item, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <Input
                    placeholder="Description (e.g., Same-day delivery Reading to Slough)"
                    value={item.description}
                    onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                    className="w-20"
                    min="1"
                  />
                  <Input
                    type="number"
                    placeholder="Price £"
                    value={item.unitPrice}
                    onChange={(e) => updateLineItem(index, 'unitPrice', e.target.value)}
                    className="w-28"
                    min="0"
                    step="0.01"
                  />
                  <div className="w-28 text-right py-2 font-medium">
                    £{((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)}
                  </div>
                  {lineItems.length > 1 && (
                    <Button
                      onClick={() => removeLineItem(index)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="border-t border-slate-200 pt-6">
            <div className="max-w-md ml-auto space-y-2">
              <div className="flex justify-between text-slate-700">
                <span>Subtotal:</span>
                <span className="font-medium">£{calculateSubtotal().toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-slate-700">
                <span>VAT (20%):</span>
                <span className="font-medium">£{calculateVAT().toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold text-emerald-700 pt-2 border-t">
                <span>Total:</span>
                <span>£{calculateTotal().toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-slate-200">
            <Button
              onClick={sendInvoiceEmail}
              disabled={isSending}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isSending ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send to Customer Email
                </>
              )}
            </Button>
            <Button
              onClick={printInvoice}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Invoice
            </Button>
            <Button
              onClick={downloadInvoice}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Preview */}
      <Card className="shadow-lg">
        <CardContent className="p-8">
          <div id="invoice-content">
            {/* Header */}
            <div className="flex justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-emerald-700 mb-2">{companyDetails.name}</h1>
                <p className="text-sm text-slate-600">{companyDetails.address}</p>
                <p className="text-sm text-slate-600">Phone: {companyDetails.phone}</p>
                <p className="text-sm text-slate-600">Email: {companyDetails.email}</p>
                <p className="text-sm text-slate-600 mt-2">Company Reg: {companyDetails.companyReg}</p>
                <p className="text-sm text-slate-600">VAT Number: {companyDetails.vatNumber}</p>
              </div>
              
              <div className="text-right">
                <h2 className="text-4xl font-bold text-slate-900 mb-4">INVOICE</h2>
                <div className="text-sm space-y-1">
                  <p><strong>Invoice #:</strong> {invoiceNumber || 'Not Set'}</p>
                  <p><strong>Date:</strong> {new Date(invoiceDate).toLocaleDateString('en-GB')}</p>
                  <p><strong>Due Date:</strong> {new Date(getDueDate()).toLocaleDateString('en-GB')}</p>
                </div>
              </div>
            </div>

            {/* Customer Details */}
            <div className="mb-8 bg-slate-50 p-4 rounded-lg">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">BILL TO:</h3>
              <p className="font-semibold text-slate-900">{customerName || 'Customer Name'}</p>
              {customerEmail && <p className="text-sm text-slate-600">{customerEmail}</p>}
              {customerPhone && <p className="text-sm text-slate-600">{customerPhone}</p>}
              {customerAddress && <p className="text-sm text-slate-600">{customerAddress}</p>}
            </div>

            {/* Line Items Table */}
            <table className="w-full mb-8">
              <thead className="bg-emerald-700 text-white">
                <tr>
                  <th className="text-left p-3">Description</th>
                  <th className="text-center p-3 w-20">Qty</th>
                  <th className="text-right p-3 w-28">Unit Price</th>
                  <th className="text-right p-3 w-28">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, index) => (
                  <tr key={index} className="border-b border-slate-200">
                    <td className="p-3">{item.description || '-'}</td>
                    <td className="p-3 text-center">{item.quantity}</td>
                    <td className="p-3 text-right">£{parseFloat(item.unitPrice || 0).toFixed(2)}</td>
                    <td className="p-3 text-right font-medium">
                      £{((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="flex justify-end mb-8">
              <div className="w-80">
                <div className="flex justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-700">Subtotal:</span>
                  <span className="font-medium">£{calculateSubtotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-700">VAT (20%):</span>
                  <span className="font-medium">£{calculateVAT().toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-3 text-xl font-bold text-emerald-700">
                  <span>Total:</span>
                  <span>£{calculateTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Bank Details */}
            <div className="bg-emerald-50 p-6 rounded-lg border border-emerald-200 mb-8">
              <h3 className="text-lg font-semibold text-emerald-900 mb-3">Payment Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-600">Account Name:</p>
                  <p className="font-semibold text-slate-900">{companyDetails.bankDetails.accountName}</p>
                </div>
                <div>
                  <p className="text-slate-600">Bank Name:</p>
                  <p className="font-semibold text-slate-900">{companyDetails.bankDetails.bankName}</p>
                </div>
                <div>
                  <p className="text-slate-600">Account Number:</p>
                  <p className="font-semibold text-slate-900">{companyDetails.bankDetails.accountNumber}</p>
                </div>
                <div>
                  <p className="text-slate-600">Sort Code:</p>
                  <p className="font-semibold text-slate-900">{companyDetails.bankDetails.sortCode}</p>
                </div>
              </div>
              <p className="text-sm text-emerald-800 mt-4 font-medium">
                Payment Terms: Due within 14 days
              </p>
            </div>

            {/* Footer */}
            <div className="text-center text-sm text-slate-500 border-t border-slate-200 pt-6">
              <p>Thank you for your business!</p>
              <p className="mt-2">For any queries regarding this invoice, please contact us at {companyDetails.phone}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #invoice-content, #invoice-content * {
            visibility: visible;
          }
          #invoice-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};
